import { useMemo, useState } from "react";
import { addMonths, startOfMonth } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../../client/components/ui/button";
import useTranslation from "../../../client/hooks/useTranslation";
import { useThemeId } from "../../../client/components/ui/card-section-header";
import { useLiteData } from "../../data/LiteDataProvider";
import { dayKey } from "../../lib/format";
import { Pnl, toneColor, type Tone } from "../../ui/ui";
import type { PfmEvent } from "./pfmModel";
import { EVENT_DOT, EVENT_STYLE, EventRow, Legend } from "./shared";

/** Calendrier mensuel des événements prop firm, avec le détail du jour choisi. */
export function EventCalendar({ events }: { events: PfmEvent[] }) {
  const t = useTranslation().lite;
  const c = t.pfm.calendar;
  const { fmt } = useLiteData();
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState<string | null>(null);
  // Tokyo Night : les jours à événement s'« allument » (liseré + halo néon).
  const neon = useThemeId() === "tokyo-night";

  const byDay = useMemo(() => {
    const m = new Map<string, PfmEvent[]>();
    for (const e of events) {
      const k = dayKey(e.date);
      const list = m.get(k);
      if (list) list.push(e); else m.set(k, [e]);
    }
    return m;
  }, [events]);

  const y = cursor.getFullYear();
  const mo = cursor.getMonth();
  const offset = (new Date(y, mo, 1).getDay() + 6) % 7;
  const n = new Date(y, mo + 1, 0).getDate();
  const cells: (Date | null)[] = [...Array<null>(offset).fill(null), ...Array.from({ length: n }, (_, i) => new Date(y, mo, i + 1))];
  while (cells.length % 7) cells.push(null);
  const todayK = dayKey(new Date());
  const monthMax = Math.max(1, ...cells.filter((d): d is Date => !!d).map(d => Math.abs((byDay.get(dayKey(d)) ?? []).reduce((s, e) => s + (e.amount ?? 0), 0))));
  const selectedEvents = selected ? byDay.get(selected) ?? [] : [];
  const move = (delta: number) => { setCursor(d => addMonths(d, delta)); setSelected(null); };

  return (
    <div>
      <div className="flex items-center gap-1 mb-4">
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(-1)} aria-label={t.common.previousMonth}><ChevronLeft size={16} /></Button>
        <div className="font-semibold w-40 text-center">{fmt.monthYear(cursor)}</div>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => move(1)} aria-label={t.common.nextMonth}><ChevronRight size={16} /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {t.common.weekdaysShort.map(d => <div key={d} className="text-[11px] text-muted-foreground/70 text-center pb-1">{d}</div>)}
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} />;
          const k = dayKey(d);
          const evs = byDay.get(k) ?? [];
          const total = evs.reduce((s, e) => s + (e.amount ?? 0), 0);
          return (
            <button
              key={k}
              type="button"
              disabled={!evs.length}
              onClick={() => setSelected(selected === k ? null : k)}
              style={neon && evs.length ? neonStyle(evs, total, monthMax) : undefined}
              className={`h-14 sm:h-20 min-w-0 rounded-lg p-1 sm:p-2 text-left flex flex-col justify-between border transition-colors ${
                selected === k ? "border-primary" : k === todayK ? "border-primary/40" : "border-transparent"
              } ${evs.length ? `${neon ? "hover:brightness-125" : "bg-muted hover:bg-muted/70"} cursor-pointer` : "bg-muted/40 cursor-default"}`}
            >
              <span className="flex items-center justify-between gap-0.5">
                <span className="text-[11px] text-muted-foreground">{d.getDate()}</span>
                <span className="hidden sm:flex gap-0.5">
                  {evs.slice(0, 3).map(e => <span key={e.id} className={`w-4 h-4 rounded grid place-items-center ${EVENT_STYLE[e.kind].cls}`}>{EVENT_STYLE[e.kind].icon}</span>)}
                </span>
              </span>
              {evs.length > 0 && (
                <span className="flex sm:hidden gap-0.5 flex-wrap">
                  {evs.slice(0, 3).map(e => <span key={e.id} className={`w-1.5 h-1.5 rounded-full ${EVENT_DOT[e.kind]}`} />)}
                </span>
              )}
              {total !== 0 && <Pnl value={total} className="hidden sm:block text-xs font-semibold truncate" />}
            </button>
          );
        })}
      </div>
      {selected && selectedEvents.length > 0 && (
        <div className="mt-4 rounded-xl border border-border overflow-hidden">
          <div className="px-5 py-2 text-xs text-muted-foreground border-b border-border">{c.dayDetail(fmt.dateLong(selectedEvents[0].date))}</div>
          <div className="divide-y divide-border">{selectedEvents.map(e => <EventRow key={e.id} e={e} />)}</div>
        </div>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-xs text-muted-foreground">
        <Legend color="bg-pnlPositive" label={c.legendPayout} />
        <Legend color="bg-pnlNegative" label={c.legendExpense} />
        <Legend color="bg-warning" label={c.legendRecurring} />
        <Legend color="bg-primary" label={c.legendFunded} />
        <Legend color="bg-muted-foreground" label={c.legendClosed} />
      </div>
    </div>
  );
}

/**
 * Case « enseigne » du thème Tokyo Night : fond d'encre, liseré à la couleur du
 * jour (jade si le solde du jour est positif, sakura s'il est négatif, violet pour
 * un passage en funded sans montant, neutre sinon) et halo sur les plus gros jours.
 */
function neonStyle(evs: PfmEvent[], total: number, monthMax: number) {
  const tone: Tone | null = total > 0 ? "gain" : total < 0 ? "loss" : evs.some(e => e.kind === "funded") ? "brand" : null;
  if (!tone) return { background: "hsl(var(--card))", boxShadow: "inset 0 0 0 1px hsl(var(--border))" };
  const strength = total !== 0 ? Math.min(1, Math.abs(total) / monthMax) : 0.6;
  const glow = strength > 0.5 ? `, 0 0 18px -5px ${toneColor(tone, 0.9)}` : "";
  return {
    background: `linear-gradient(160deg, ${toneColor(tone, 0.08 + 0.22 * strength)}, transparent 75%), hsl(var(--card))`,
    boxShadow: `inset 0 0 0 1px ${toneColor(tone, 0.35 + 0.6 * strength)}${glow}`,
  };
}
